import { Typography } from "@mui/material";
import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TableColumn, TableComponent } from "@/components";
import { AiModelUseCase, CompanyCosts } from "@/pages/Private/redux/media/media.schema";

interface CompanyCostsBreakdownProps {
	costs?: CompanyCosts;
	isFetching: boolean;
}

const formatUsd = (amount: number) =>
	`$${amount.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * `TableComponent` keys its rows on `id`, and none of these breakdowns has one — they are
 * groupings, not records. Each borrows the field it is grouped by, which is unique within
 * its own table by construction.
 */
type CourseRow = { id: number; courseId: number; courseName: string; amountUsd: number };
type UseCaseRow = { id: string; useCase: AiModelUseCase; amountUsd: number };
type MonthRow = { id: string; month: string; amountUsd: number };

/**
 * The three breakdowns, under the total they add up to — with one exception the page says
 * out loud.
 *
 * `byCourse` can sum to less than the total: a generated music track is billed to the
 * company with no scenario attached. Presenting a breakdown that quietly does not add up
 * would be worse than the extra line of copy.
 */
export const CompanyCostsBreakdown: FunctionComponent<CompanyCostsBreakdownProps> = ({
	costs,
	isFetching,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`mediaCosts.${key}`, options ?? {});

	const courseColumns: TableColumn<CourseRow>[] = useMemo(
		() => [
			{
				label: ts("columns.scenario"),
				minWidth: 240,
				format: (row) => (
					<Typography className="text-ink" variant="subtitle2">
						{row.courseName || ts("deletedScenario", { id: row.courseId })}
					</Typography>
				),
			},
			{
				label: ts("columns.amount"),
				minWidth: 120,
				align: "right",
				format: (row) => formatUsd(row.amountUsd),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	const useCaseColumns: TableColumn<UseCaseRow>[] = useMemo(
		() => [
			{
				label: ts("columns.useCase"),
				minWidth: 240,
				format: (row) => t(`mediaPresets.useCase.${row.useCase.toLowerCase()}`),
			},
			{
				label: ts("columns.amount"),
				minWidth: 120,
				align: "right",
				format: (row) => formatUsd(row.amountUsd),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	const monthColumns: TableColumn<MonthRow>[] = useMemo(
		() => [
			{ label: ts("columns.month"), minWidth: 240, format: (row) => row.month },
			{
				label: ts("columns.amount"),
				minWidth: 120,
				align: "right",
				format: (row) => formatUsd(row.amountUsd),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	const courseRows: CourseRow[] = (costs?.byCourse ?? []).map((row) => ({
		...row,
		id: row.courseId,
	}));
	const useCaseRows: UseCaseRow[] = (costs?.byUseCase ?? []).map((row) => ({
		...row,
		id: row.useCase,
	}));
	const monthRows: MonthRow[] = (costs?.byMonth ?? []).map((row) => ({ ...row, id: row.month }));

	const byCourseTotal = (costs?.byCourse ?? []).reduce((sum, row) => sum + row.amountUsd, 0);
	// A tenth of a cent of float noise is not "spend without a scenario".
	const hasCourselessSpend = !!costs && costs.totalUsd - byCourseTotal > 0.005;

	const table = <TRow extends { id: string | number }>(
		titleKey: string,
		columns: TableColumn<TRow>[],
		rows: TRow[],
		hint?: string
	) => (
		<section className="rounded-card border border-border-soft bg-white p-5">
			<h3 className="text-ink">{ts(titleKey)}</h3>
			{!!hint && <p className="mt-1 text-sm text-subtle">{hint}</p>}

			<div className="mt-4">
				<TableComponent<TRow>
					hidePagination
					columns={columns}
					data={rows}
					handleChangeLimit={() => undefined}
					handleChangePage={() => undefined}
					isLoading={isFetching}
					itemCount={rows.length}
					limit={rows.length || 1}
					noDataText={ts("empty")}
					page={0}
				/>
			</div>
		</section>
	);

	return (
		<div className="space-y-5">
			<section className="flex flex-wrap items-baseline justify-between gap-3 rounded-card bg-accent-tint px-5 py-4">
				<span className="text-sm font-bold text-ink">{ts("total")}</span>
				<span className="text-2xl font-bold text-ink">{formatUsd(costs?.totalUsd ?? 0)}</span>
			</section>

			{table<CourseRow>(
				"byCourse",
				courseColumns,
				courseRows,
				hasCourselessSpend
					? ts("courselessHint", { amount: formatUsd(costs!.totalUsd - byCourseTotal) })
					: undefined
			)}
			{table<UseCaseRow>("byUseCase", useCaseColumns, useCaseRows)}
			{table<MonthRow>("byMonth", monthColumns, monthRows)}
		</div>
	);
};
